import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export const researchPapers = [
  {
    title: 'The Future of AI: Trends and Predictions',
    pdfUrl: null, // 'pdfs/reagan-cv.pdf',
    expectedImpact: 'High - This paper explores the future trajectory of AI development and its societal implications.',
  },
  {
    title: 'Advancements in Natural Language Processing',
    pdfUrl: null,
    expectedImpact: 'Medium - This article reviews recent breakthroughs in NLP and their applications across industries.',
  },
];

const PdfViewer = ({ file, title, className = 'h-[32rem]', interactive = false }) => {
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const viewerRef = useRef(null);

  useEffect(() => {
    if (!viewerRef.current) {
      return undefined;
    }

    const updateWidth = () => {
      setContainerWidth(viewerRef.current?.clientWidth || 0);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(viewerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const viewerClasses = interactive
    ? `${className} cursor-pointer overflow-y-auto rounded-md transition-all duration-300 hover:ring-2 hover:ring-emerald-400/60`
    : `${className} overflow-y-auto rounded-md`;

  return (
    <div ref={viewerRef} className={viewerClasses}>
      <Document
        file={file}
        onLoadSuccess={({ numPages: loadedPages }) => setNumPages(loadedPages)}
        loading={<p className="p-4 text-sm text-gray-300">Loading PDF...</p>}
        error={
          <p className="p-4 text-sm text-red-300">
            PDF failed to load. Check that the file path is correct and the file is accessible.
          </p>
        }
        noData={<p className="p-4 text-sm text-gray-300">No PDF file selected.</p>}
      >
        <div className="space-y-4">
          {Array.from(new Array(numPages), (_, index) => (
            <div
              key={`${title}-page-${index + 1}`}
              className="overflow-hidden rounded-md border border-gray-800 bg-white shadow-lg"
            >
              <Page
                pageNumber={index + 1}
                width={containerWidth > 0 ? Math.max(containerWidth - 2, 280) : 280}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </div>
          ))}
        </div>
      </Document>
    </div>
  );
};

const ResearchCard = ({ research, onOpen }) => {
  return (
    <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm border border-gray-700/50 rounded-xl shadow-2xl p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-emerald-500/20 hover:border-emerald-500/50">
      <h3 className="text-xl font-semibold text-white mb-4 leading-tight">
        {research.title}
      </h3>

      <div className="mb-4 rounded-lg border border-gray-700/70 bg-slate-950 p-3">
        {research.pdfUrl ? (
          <button
            type="button"
            onClick={() => onOpen(research)}
            className="block w-full text-left"
            aria-label={`Open ${research.title} in full screen reader`}
          >
            <PdfViewer
              file={research.pdfUrl}
              title={research.title}
              className="h-[32rem]"
              interactive
            />
          </button>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-600 bg-slate-950/60 p-4 text-sm text-gray-300">
            PDF not available or failed to load for this research paper.
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="text-sm">
          <span className="text-emerald-400 font-medium">Impact:</span>
          <span className="text-gray-300 ml-2">{research.expectedImpact}</span>
        </div>
        {research.pdfUrl ? (
          <button
            type="button"
            onClick={() => onOpen(research)}
            className="w-full rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 px-4 py-3 text-sm font-semibold text-white transition-all duration-300 hover:from-emerald-400 hover:to-cyan-400"
          >
            Read Paper
          </button>
        ) : null}
      </div>
    </div>
  );
};

const ResearchModal = ({ research, onClose }) => {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  if (!research) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 p-4 md:p-8">
      <div className="flex h-full w-full max-w-7xl flex-col overflow-hidden rounded-2xl border border-gray-700/70 bg-gradient-to-br from-slate-900 to-slate-950 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-800 px-5 py-4">
          <div className="pr-4">
            <h2 className="text-lg font-semibold text-white">{research.title}</h2>
            <p className="text-sm text-gray-400">Full-screen paper reader</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-700 bg-gray-900 px-4 py-2 text-sm font-medium text-gray-200 transition-colors hover:border-emerald-500/50 hover:text-white"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 p-4 md:p-6">
          <div className="h-full rounded-xl border border-gray-800 bg-slate-950 p-3 md:p-4">
            <PdfViewer file={research.pdfUrl} title={research.title} className="h-full" />
          </div>
        </div>
      </div>
    </div>
  );
};

const Research = () => {
  const [selectedResearch, setSelectedResearch] = useState(null);

  return (
    <>
      <div className="min-h-screen p-8 pt-24 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-900">
        <div className="container mx-auto">
          <h1 className="text-5xl font-bold text-center bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-12">
            Academic Research Papers and Articles
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-8">
            {researchPapers.map((research, index) => (
              <ResearchCard
                key={index}
                research={research}
                onOpen={setSelectedResearch}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedResearch ? (
        <ResearchModal
          research={selectedResearch}
          onClose={() => setSelectedResearch(null)}
        />
      ) : null}
    </>
  );
};

export default Research;
